import { Link, useParams, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articleCategories, allArticles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeSlug = slug || articleCategories[0].slug;

  const currentCategory = articleCategories.find((c) => c.slug === activeSlug) || articleCategories[0];
  const categoryArticles = allArticles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 专业服务与行业洞察`}
        description={`果冻传媒${currentCategory.name}服务，${currentCategory.description}。浏览果冻传媒在${currentCategory.name}领域的最新案例与专业文章。`}
        keywords={`果冻传媒${currentCategory.name},${currentCategory.name}服务,品牌${currentCategory.name}`}
        canonicalUrl={`https://www.guodongmedia.com/category/${activeSlug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.guodongmedia.com/' },
              { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://www.guodongmedia.com/category/${activeSlug}` },
            ],
          }),
        }}
      />
      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: currentCategory.name }]} />
            <div className="py-10 md:py-14 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-accent-600 text-2xl`}></i>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
                {currentCategory.name}
              </h1>
              <p className="text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <section className="py-8 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 flex-wrap mb-10 justify-center">
              {articleCategories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center flex-wrap gap-2 mt-4 pt-4 border-t border-background-200/70">
                      {article.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}